import React, { useState, useEffect, useRef, useCallback } from "react";
import DataTable from "../../components/common/DataTable/DataTable";
import { useNavigate } from "react-router-dom";
import addIcon from "../../assets/images/icons/addIcon.png";
import filterIcon from "../../assets/images/icons/filterIcon.svg";
import searchIcon from "../../assets/images/icons/search.png";

import "./MyTasks.css";
import { nanoid } from "@reduxjs/toolkit";
import FilterCoreModal from "../../components/common/FilterCoreModal/FilterCoreModal";

import {
  useGetTasksQuery,
  useGetTaskStatusQuery,
  useGetTaskInsightsQuery,
} from "./taskSlice";
import { selectCurrentUser } from "../auth/authSlice";
import { useSelector } from "react-redux";
import { Loader } from "../../components/common/Loader/Loader";
import { taskStatus } from "../../enums/statuses";
import { useGetIncidentGroupsQuery } from "../Incidents/IncidentSlice";
import { hasAnyValue, sanitizeInputValue } from "../../utils/commonMethods";
import UserSelect from "../../components/common/UserSelect/UserSelect";
import AreaSelect from "../../components/common/Selectors/AreaSelect";
import SubDepartmentSelect from "../../components/common/Selectors/SubDepartmentSelect";
import DepartmentSelect from "../../components/common/Selectors/DepartmentSelect";
import CostCentreSelect from "../../components/common/Selectors/CostCentreSelect";
const MyTasks = () => {
  const navigate = useNavigate();

  const initialFilters = localStorage.getItem("taskfilters")
    ? JSON.parse(localStorage.getItem("taskfilters"))
    : null;

  const user = useSelector(selectCurrentUser);

  const [tasksData, setTasksData] = useState([]);
  const [taskColumns, setTaskColumns] = useState([]);
  const [taskStatusData, setTaskStatusData] = useState([]);
  const [insightsData, setInsightsData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);

  const [filterValues, setFilterValues] = useState({
    ...initialFilters,
    departmentId: initialFilters?.departmentId?.value,
    subDepartmentId: initialFilters?.subDepartmentId?.value,
    areaId: initialFilters?.areaId?.value,
    costCenterId: initialFilters?.costCenterId?.value,
    assignedTo: initialFilters?.assignedTo?.value,
    createdById: initialFilters?.createdById?.value,
    seniorMangerId: initialFilters?.seniorMangerId?.value,
    localMangerId: initialFilters?.localMangerId?.value,
    incidentAdvisorId: initialFilters?.incidentAdvisorId?.value,
  });

  const [insightsStatus, setInsightsStatus] = useState(
    initialFilters?.insightsStatus || ""
  );

  const [filter, setFilter] = useState({
    statusId: initialFilters?.statusId,
    assignedTo: initialFilters?.assignedTo,
    typeId: initialFilters?.typeId,
    subTypeId: initialFilters?.subTypeId,
    groupId: initialFilters?.groupId,
    departmentId: initialFilters?.departmentId,
    subDepartmentId: initialFilters?.subDepartmentId,
    areaId: initialFilters?.areaId,
    costCenterId: initialFilters?.costCenterId,
    createdById: initialFilters?.createdById,
    localMangerId: initialFilters?.localMangerId,
    seniorMangerId: initialFilters?.seniorMangerId,
    incidentAdvisorId: initialFilters?.incidentAdvisorId,
  });

  const [incidentGroupData, setIncidentGroupData] = useState([]);
  const [incidentTaskOrderBy, setIncidentTaskOrderBy] = useState("");
  const [orderByType, setOrderByType] = useState("Desc");
  const [isAscending, setIsAscending] = useState(false);
  const [query, setQuery] = useState("");

  const {
    data: tasks,
    isSuccess,
    isFetching,
  } = useGetTasksQuery({
    role: user.roles,
    page: pageNumber,
    query,
    incidentTaskOrderBy,
    orderByType,
    ...filterValues,
  });

  const {
    data: statuses,
    // isLoading: statusesIsLoading,
    // isSuccess: statusesIsSuccess,
    // isError: statusesIsError,
  } = useGetTaskStatusQuery();

  const {
    data: insights,
    // isLoading: insightsIsLoading,
    // isSuccess: insightsIsSuccess,
    // isError: insightsIsError,
  } = useGetTaskInsightsQuery();

  const {
    data: incidentGroups,
    // isLoading: incidentGroupsIsLoading,
    // isSuccess: incidentGroupsIsSuccess,
    // isError: incidentGroupsIsError,
  } = useGetIncidentGroupsQuery();

  const generateColumns = useCallback(
    (data) => {
      if (data.length <= 0) {
        return;
      }

      const columns = Object.keys(data[0])
        .map((field) => {
          if (["pageSettings", "progress", "incident"].includes(field)) {
            return null;
          }

          if (field === "guid") return null;

          let columnConfig = {
            key: nanoid(),
            field,
            headerText: field
              .replace(/([A-Z])/g, " $1")
              .trim()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            alignment: "left",
            template: null,
            headerTemplate: null,
            width: "180px",
            allowSorting: true,
            order: null,
          };

          if (field === "id") {
            columnConfig.headerText = "ID";
            columnConfig.width = "100px";
            columnConfig.template = (props) => (
              <p className="m-0">{props[field]}</p>
            );
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">
                {props.headerText}
                {props.field.toLowerCase() === incidentTaskOrderBy.toLowerCase()
                  ? isAscending
                    ? "↓"
                    : "↑"
                  : ""}
              </span>
            );
            columnConfig.order = 1;
          }

          if (field === "name") {
            columnConfig.headerText = "Task Name";
            columnConfig.template = (props) => (
              <p className="m-0 fw-bold ">{props[field]}</p>
            );
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">
                {props.headerText}
                {props.field.toLowerCase() === incidentTaskOrderBy.toLowerCase()
                  ? isAscending
                    ? "↓"
                    : "↑"
                  : ""}
              </span>
            );
            columnConfig.order = 2;
            columnConfig.width = "400px";
          }

          if (field === "location") {
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">{props.headerText}</span>
            );
            columnConfig.order = 3;
            columnConfig.width = "200px";
          }

          if (field === "createdOn") {
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">
                {props.headerText}
                {props.field.toLowerCase() === incidentTaskOrderBy.toLowerCase()
                  ? isAscending
                    ? "↓"
                    : "↑"
                  : ""}
              </span>
            );
            columnConfig.order = 4;
          }

          if (field === "dueOn") {
            columnConfig.allowSorting = false;
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">{props.headerText}</span>
            );
            columnConfig.order = 5;
          }

          if (field === "assignedTo") {
            columnConfig.headerText = "Assigned To";
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">
                {props.headerText}
                {incidentTaskOrderBy === "AssignedToId"
                  ? isAscending
                    ? "↓"
                    : "↑"
                  : ""}
              </span>
            );
            columnConfig.template = (props) => (
              <div className="d-flex gap-1 flex-wrap">
                {props.assignedTo.map((assignee) => (
                  <span
                    key={assignee.id}
                    className="me-1 bg-success text-white p-1 rounded"
                  >
                    {assignee.name}
                  </span>
                ))}
              </div>
            );
            columnConfig.width = "260px";
            columnConfig.order = 6;
          }

          if (field === "status") {
            columnConfig.allowSorting = false;
            columnConfig.template = (props) => {
              let style;
              switch (props.status?.id) {
                case taskStatus.COMPLETED:
                  style = "status-pill complete";
                  break;
                case taskStatus.INPROGRESS:
                  style = "status-pill ongoing";
                  break;
                default:
                  style = "";
              }
              return <span className={style}>{props[field]?.description}</span>;
            };
            columnConfig.headerTemplate = (props) => (
              <span className="m-0">{props.headerText}</span>
            );
            columnConfig.order = 7;
          }

          return columnConfig;
        })
        .filter(Boolean)
        .sort(
          (a, b) =>
            (a.order ?? Number.MAX_VALUE) - (b.order ?? Number.MAX_VALUE)
        );

      return columns;
    },
    [incidentTaskOrderBy, isAscending]
  );

  useEffect(() => {
    if (tasks) {
      const data = tasks.ids.map((id) => {
        return tasks.entities[id];
      });

      setTasksData(data);
      setTaskColumns(generateColumns(data));
    }
  }, [tasks, generateColumns]);

  useEffect(() => {
    if (statuses) {
      const data = statuses.ids.map((id) => {
        return statuses.entities[id];
      });
      setTaskStatusData(data);
    }
  }, [statuses]);

  useEffect(() => {
    if (insights) {
      const data = insights.ids.map((id) => {
        return insights.entities[id];
      });
      setInsightsData(...data);
    }
  }, [insights]);

  useEffect(() => {
    if (incidentGroups) {
      const data = incidentGroups.ids.map((id) => {
        return incidentGroups.entities[id];
      });
      setIncidentGroupData(data);
    }
  }, [incidentGroups]);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  let gridRef = useRef(null);

  const handleSearch = (e) => {
    setPageNumber(1);
    const inputValue = sanitizeInputValue(e.target.value);
    setQuery(inputValue);
  };

  const handlePageChange = (page) => {
    if (page !== undefined || page !== null) {
      setPageNumber(page);
    }
  };

  const getGroups = () => {
    return incidentGroupData?.map((group) => (
      <option key={group.id} value={group.id}>
        {group.description}
      </option>
    ));
  };

  const getTypes = () => {
    if (!filter.groupId) return [];
    const group = incidentGroupData?.find(
      (group) => group.id === parseInt(filter.groupId)
    );
    return group
      ? group.types.map((type) => (
          <option key={type.id} value={type.id}>
            {type.description}
          </option>
        ))
      : [];
  };

  const getSubTypes = () => {
    if (!filter.typeId) return [];
    const group = incidentGroupData?.find(
      (group) => group?.id === parseInt(filter.groupId)
    );
    const type = group?.types?.find(
      (type) => type?.id === parseInt(filter.typeId)
    );
    return type
      ? type.subTypes.map((subType) => (
          <option key={subType?.id} value={subType?.id}>
            {subType?.description}
          </option>
        ))
      : [];
  };

  const handleFilter = (e) => {
    e.preventDefault();

    let value = "";

    if (e.target.overdue.checked) {
      value = "overdue";
      setInsightsStatus(value);
    }
    if (e.target.duetoday.checked) {
      value = "duetoday";
      setInsightsStatus(value);
    }
    if (e.target.upcoming.checked) {
      value = "upcoming";
      setInsightsStatus(value);
    }

    const filters = {
      ...filter,
      departmentId: filter?.departmentId?.value,
      subDepartmentId: filter?.subDepartmentId?.value,
      areaId: filter?.areaId?.value,
      costCenterId: filter?.costCenterId?.value,
      assignedTo: filter?.assignedTo?.value,
      createdById: filter?.createdById?.value,
      seniorMangerId: filter?.seniorMangerId?.value,
      localMangerId: filter?.localMangerId?.value,
      incidentAdvisorId: filter?.incidentAdvisorId?.value,
      insightsStatus: value,
    };

    setFilterValues(filters);
    localStorage.setItem(
      "taskfilters",
      JSON.stringify({
        ...filters,
        departmentId: filter?.departmentId,
        subDepartmentId: filter?.subDepartmentId,
        areaId: filter?.areaId,
        costCenterId: filter?.costCenterId,
        assignedTo: filter?.assignedTo,
        createdById: filter?.createdById,
        seniorMangerId: filter?.seniorMangerId,
        localMangerId: filter?.localMangerId,
        incidentAdvisorId: filter?.incidentAdvisorId,
      })
    );
    setPageNumber(1);
    toggleModal();
  };

  const handleReset = () => {
    setFilter({
      statusId: "",
      assignedTo: "",
      typeId: "",
      subTypeId: "",
      groupId: "",
      departmentId: "",
      subDepartmentId: "",
      areaId: "",
      costCenterId: "",
      createdById: "",
      localMangerId: null,
      seniorMangerId: null,
      incidentAdvisorId: null,
    });
    setInsightsStatus("");
    setFilterValues({});
    localStorage.removeItem("taskfilters");
    setPageNumber(1);
  };

  const goToViewTaskPage = (args) => {
    navigate(`view/${args?.data?.guid}`);
  };

  const handleQuickFilter = (value) => {
    const data = {
      statusId: "",
      assignedTo: "",
      typeId: "",
      subTypeId: "",
      groupId: "",
    };
    setInsightsStatus(value);
    setFilterValues({ ...data, insightsStatus: value });

    const taskFilters = JSON.parse(localStorage.getItem("taskfilters"));

    localStorage.setItem(
      "taskfilters",
      JSON.stringify({ ...taskFilters, insightsStatus: value })
    );
    setPageNumber(1);
  };

  const handleSorting = (args) => {
    if (args.requestType === "sorting") {
      const columnName = args.columnName;
      // const direction = args.direction;

      switch (columnName) {
        case "id":
          setIncidentTaskOrderBy("Id");
          break;
        case "name":
          setIncidentTaskOrderBy("Name");
          break;
        case "createdOn":
          setIncidentTaskOrderBy("CreatedOn");
          break;
        case "assignedTo":
          setIncidentTaskOrderBy("AssignedToId");
          break;
        default:
          setIncidentTaskOrderBy("");
      }

      if (isAscending) {
        setOrderByType("Asc");
        setIsAscending((prev) => !prev);
      } else {
        setOrderByType("Desc");
        setIsAscending((prev) => !prev);
      }
      args.cancel = true;
    }
  };

  const handleFilterValueChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="my-tasks">
      <div className="row mb-1 justify-content-between align-items-center">
        <div className="col-auto">
          <h4 className="p-2 m-0">My Tasks</h4>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-green btn-icon px-5"
            onClick={() => navigate("/incidents/new")}
          >
            <span className="me-2"> New Incident</span>
            <img src={addIcon} width={22} alt="" />
          </button>
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-12 col-md mb-1">
          <div
            className="card custom-card img-card inprogress-card"
            role="button"
            onClick={() => handleQuickFilter("overdue")}
          >
            <div className="card-body custom-card-body">
              <p className="fs-4 text-uppercase">Overdue</p>
              <h3> {insightsData?.overdueCount} </h3>
            </div>
          </div>
        </div>
        <div className="col-12 col-md  mb-1">
          <div
            className="card custom-card img-card completed-card"
            role="button"
            onClick={() => handleQuickFilter("duetoday")}
          >
            <div className="card-body custom-card-body">
              <p className="fs-4 text-uppercase">Due Today</p>
              <h3>{insightsData?.dueTodayCount} </h3>
            </div>
          </div>
        </div>
        <div className="col-12 col-md  mb-1">
          <div
            className="card custom-card img-card upcoming-card"
            role="button"
            onClick={() => handleQuickFilter("upcoming")}
          >
            <div className="card-body custom-card-body">
              <p className="fs-4 text-uppercase">Upcoming</p>
              <h3>{insightsData?.upcomingCount} </h3>
            </div>
          </div>
        </div>
      </div>
      <div className="card custom-card">
        <div className="card-body">
          <div className="row justify-content-between align-items-center mb-3">
            <div className="col-auto">
              <h6 className="d-flex align-items-center gap-2">
                Task List
                {filterValues && hasAnyValue(filterValues) && (
                  <span className="badge rounded-pill text-bg-warning">
                    Filters applied
                  </span>
                )}
              </h6>
            </div>
            <div className="col-auto">
              <div className="d-flex align-items-center">
                <div className="input-group">
                  <label
                    htmlFor="searchTask"
                    className="input-group-text search-icon-container"
                  >
                    <img
                      src={searchIcon}
                      alt="Search Icon"
                      className="icon-image"
                      width={12}
                    />
                  </label>
                  <input
                    id="searchTask"
                    type="text"
                    className="form-control search"
                    placeholder="Search Tasks"
                    onKeyUp={handleSearch}
                  />
                </div>
                <button className="btn btn-filter" onClick={toggleModal}>
                  <img src={filterIcon} width={34} alt="" />
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {isFetching ? (
                <Loader />
              ) : (
                isSuccess &&
                (tasksData.length > 0 ? (
                  <DataTable
                    data={tasksData}
                    columns={taskColumns}
                    grid={gridRef}
                    handlePageChange={handlePageChange}
                    currentPage={pageNumber}
                    defaultPaging={false}
                    isDetailTemplate={false}
                    rowSelected={goToViewTaskPage}
                    handleSorting={handleSorting}
                    allowSorting={true}
                    height={"calc(100vh - 480px)"}
                  />
                ) : (
                  "No Tasks Available..."
                ))
              )}
            </div>
          </div>
          <FilterCoreModal
            isOpen={modalIsOpen}
            toggle={toggleModal}
            title={"Task Filters"}
          >
            <form onSubmit={handleFilter}>
              <div className="mb-3">
                <label htmlFor="statusId" className="custom-label">
                  Status
                </label>
                <select
                  id="statusId"
                  name="statusId"
                  className="form-select custom-field"
                  aria-labelledby="status-label"
                  value={filter.statusId}
                  onChange={handleFilterValueChange}
                >
                  <option value={""}>Select Status</option>
                  {taskStatusData?.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <UserSelect
                  value={filter.assignedTo}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      assignedTo: value,
                    }))
                  }
                  label={"Assigned To"}
                  placeholder="Select assignee"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="groupId" className="custom-label">
                  Group
                </label>
                <select
                  id="groupId"
                  name="groupId"
                  className="form-control custom-field"
                  value={filter.groupId}
                  onChange={handleFilterValueChange}
                >
                  <option value="">Select Group</option>
                  {getGroups()}
                </select>
              </div>

              {filter.groupId && (
                <div className="mb-3">
                  <label htmlFor="typeId" className="custom-label">
                    Type
                  </label>
                  <select
                    id="typeId"
                    name="typeId"
                    className="form-control custom-field"
                    value={filter.typeId}
                    onChange={handleFilterValueChange}
                  >
                    <option value="">Select Type</option>
                    {getTypes()}
                  </select>
                </div>
              )}

              {filter.typeId && (
                <div className="mb-3">
                  <label htmlFor="subTypeId" className="custom-label">
                    Sub Type
                  </label>
                  <select
                    id="subTypeId"
                    name="subTypeId"
                    className="form-control custom-field"
                    value={filter.subTypeId}
                    onChange={handleFilterValueChange}
                  >
                    <option value="">Select Sub Type</option>
                    {getSubTypes()}
                  </select>
                </div>
              )}

              <div>
                <label className="custom-label">Location</label>
                <div className="d-flex gap-2 mb-3">
                  <div className="w-100">
                    <DepartmentSelect
                      value={filter.departmentId}
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          departmentId: value,
                        }))
                      }
                      placeholder="Department"
                    />
                  </div>
                  <div className="w-100">
                    <SubDepartmentSelect
                      value={filter.subDepartmentId}
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          subDepartmentId: value,
                        }))
                      }
                      placeholder="Sub Department"
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <div className="w-100">
                    <AreaSelect
                      value={filter.areaId}
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          areaId: value,
                        }))
                      }
                      placeholder="Area"
                    />
                  </div>
                  <div className="w-100">
                    <CostCentreSelect
                      value={filter.costCenterId}
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          costCenterId: value,
                        }))
                      }
                      placeholder="Cost Centre"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <UserSelect
                  value={filter.createdById}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      createdById: value,
                    }))
                  }
                  label={"Submitted By"}
                  placeholder="Select submitted by"
                />
              </div>
              <div className="mb-3">
                <UserSelect
                  value={filter.localMangerId}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      localMangerId: value,
                    }))
                  }
                  label={"Local Incident Manager"}
                  placeholder="Select Local Incident Manager"
                />
              </div>
              <div className="mb-3">
                <UserSelect
                  value={filter.seniorMangerId}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      seniorMangerId: value,
                    }))
                  }
                  label={"Senior Incident Manager"}
                  placeholder="Select Senior Incident Manager"
                />
              </div>
              <div className="mb-3">
                <UserSelect
                  value={filter.incidentAdvisorId}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      incidentAdvisorId: value,
                    }))
                  }
                  label={"Incident Manager (Advisor/Lead)"}
                  placeholder="Select Incident Manager (Advisor/Lead)"
                />
              </div>

              <div className="mb-3">
                <fieldset>
                  <legend className="custom-label">Insight Status</legend>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="insightStatus"
                      id="overdue"
                      defaultChecked={insightsStatus === "overdue"}
                      aria-checked={insightsStatus === "overdue"}
                    />
                    <label className="form-check-label" htmlFor="overdue">
                      Overdue
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="insightStatus"
                      id="duetoday"
                      defaultChecked={insightsStatus === "duetoday"}
                      aria-checked={insightsStatus === "duetoday"}
                    />
                    <label className="form-check-label" htmlFor="duetoday">
                      Due Today
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="insightStatus"
                      id="upcoming"
                      defaultChecked={insightsStatus === "upcoming"}
                      aria-checked={insightsStatus === "upcoming"}
                    />
                    <label className="form-check-label" htmlFor="upcoming">
                      Upcoming
                    </label>
                  </div>
                </fieldset>
              </div>

              <div className="row gap-1">
                <div className="col">
                  <button type="submit" className="btn w-100 btn-green">
                    Apply Filters
                  </button>
                </div>
                <div className="col">
                  <button
                    type="reset"
                    className="btn w-100 btn-grey"
                    onClick={handleReset}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </form>
          </FilterCoreModal>
        </div>
      </div>
    </div>
  );
};

export default MyTasks;
